import { useEffect, useMemo, useState } from "react";
import PaginationControls from "../../../components/base/PaginationControls";
import Select from "react-select";
import Swal from "sweetalert2";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { useQueryClient } from "@tanstack/react-query";
import {
  useTruckYardStore,
  type TruckYardVehicle,
} from "../../../store/truckYardStore";
import { useGroupsStore } from "../../../store/admin/groupsStore";
import { useAllTruckYardVehicles } from "../../../features/truckYard/queries";
import { useAllGroups } from "../../../features/catalogs/queries";
import useTruckYardRole from "../../../hooks/useTruckYardRole";
import { useTranslation } from "react-i18next";
import { assignVehicleErrorKey, canEditAccessControlId } from "./vehicleAssignment";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import HeaderComponent from "../components/HeaderComponent";
import {
  NO_YARD_FILTER,
  matchesYardSelection,
} from "../components/yardMultiFilter";
import NoAssignedYardsNotice from "../../../components/base/NoAssignedYardsNotice";
import { getWaterTypeLabel } from "../components/waterTypes";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

interface YardOption {
  value: string;
  label: string;
}

const PAGE_SIZE = 6;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function vehicleLabel(v: TruckYardVehicle) {
  if (v.plate && v.name) return `${v.name} · ${v.plate}`;
  return v.name || v.plate || v.id;
}

function vehicleSubtitle(v: TruckYardVehicle) {
  const bits = [v.brand, v.model, v.year ? String(v.year) : ""].filter(Boolean);
  return bits.join(" · ") || "Sin información adicional";
}

function statusName(v: TruckYardVehicle): string {
  if (typeof v.status === "string") return v.status;
  return v.status?.name || "";
}

function isActive(v: TruckYardVehicle) {
  const s = statusName(v).toLowerCase();
  return s === "active" || s === "activo";
}

// Resolve which water types should be displayed for a vehicle.
//   - If the vehicle has a saved subset (`v.truck_yard_types`), intersect it with
//     the current inherited pool — so types dropped from the group fall off,
//     and types not yet in any group can never appear.
//   - Otherwise (no override yet) show all inherited types.
//   - If the intersection collapses to empty (e.g., group dropped every
//     stored type), fall back to inherited to honour the "never empty"
//     invariant the assignment modal enforces on new edits.
function effectiveTypesFor(v: TruckYardVehicle, inherited: string[]): string[] {
  if (!Array.isArray(v.type)) return inherited;
  const filtered = v.type.filter((t) => inherited.includes(t));
  return filtered.length > 0 ? filtered : inherited;
}

// ─── Grid Card ───────────────────────────────────────────────────────────────

function VehicleGridCard({
  vehicle,
  yardsById,
  vehicleTypes,
  themeClasses,
  isDarkMode,
  onAssign,
}: {
  vehicle: TruckYardVehicle;
  yardsById: Record<string, string>;
  vehicleTypes: string[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onAssign: (v: TruckYardVehicle) => void;
}) {
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const yards = vehicle.yard_ids || [];

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all duration-200 flex flex-col gap-3`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-teal-500/20" : "bg-teal-50"
            }`}
          >
            <i className="ri-truck-line text-teal-500 text-lg"></i>
          </div>
          <div className="min-w-0">
            <h3
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {vehicleLabel(vehicle)}
            </h3>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {vehicle.id}
            </span>
          </div>
        </div>
        <span
          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${
            isActive(vehicle) ? "bg-green-500" : "bg-gray-400"
          }`}
        />
      </div>

      <div className={`${surface} rounded-xl p-3 space-y-2`}>
        <div className="flex items-center gap-2">
          <i
            className={`ri-information-line text-xs ${themeClasses.textSecondary}`}
          ></i>
          <span className={`text-xs ${themeClasses.textSecondary} truncate`}>
            {vehicleSubtitle(vehicle)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <i
            className={`ri-key-2-line text-xs ${themeClasses.textSecondary}`}
          ></i>
          <span
            className={`text-xs ${themeClasses.text} font-mono truncate`}
            title={vehicle.accessControl_id || ""}
          >
            {vehicle.accessControl_id || "Sin ID de acceso"}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <i
            className={`ri-building-4-line text-xs ${themeClasses.textSecondary} mt-0.5`}
          ></i>
          <div className="flex flex-wrap gap-1">
            {yards.length === 0 ? (
              <span className={`text-xs italic ${themeClasses.textSecondary}`}>
                Sin patios asignados
              </span>
            ) : (
              yards.map((id) => (
                <span
                  key={id}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${
                    isDarkMode
                      ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                      : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}
                >
                  {yardsById[id] || id}
                </span>
              ))
            )}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <i
            className={`ri-drop-line text-xs ${themeClasses.textSecondary} mt-0.5`}
          ></i>
          <div className="flex flex-wrap gap-1">
            {vehicleTypes.length === 0 ? (
              <span className={`text-xs italic ${themeClasses.textSecondary}`}>
                Sin tipo heredado
              </span>
            ) : (
              vehicleTypes.map((t) => (
                <span
                  key={t}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${
                    isDarkMode
                      ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                      : "bg-cyan-50 text-cyan-700 border border-cyan-200"
                  }`}
                >
                  {getWaterTypeLabel(t)}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => onAssign(vehicle)}
          className="flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors text-center"
          style={{ backgroundColor: brand.primary, color: brand.accent }}
        >
          <i className="ri-links-line mr-1"></i>
          Asignar
        </button>
      </div>
    </div>
  );
}

// ─── List Row ────────────────────────────────────────────────────────────────

function VehicleListRow({
  vehicle,
  yardsById,
  vehicleTypes,
  themeClasses,
  isDarkMode,
  onAssign,
}: {
  vehicle: TruckYardVehicle;
  yardsById: Record<string, string>;
  vehicleTypes: string[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onAssign: (v: TruckYardVehicle) => void;
}) {
  const yards = vehicle.yard_ids || [];

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-200`}
    >
      {/* Mobile */}
      <div className="flex flex-col gap-3 sm:hidden">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-teal-500/20" : "bg-teal-50"
            }`}
          >
            <i className="ri-truck-line text-teal-500 text-base"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {vehicleLabel(vehicle)}
            </p>
            <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
              {vehicleSubtitle(vehicle)}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span
            className={`text-xs font-mono ${themeClasses.textSecondary} truncate max-w-[60%]`}
          >
            {vehicle.accessControl_id || "Sin ID de acceso"}
          </span>
          <button
            onClick={() => onAssign(vehicle)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ backgroundColor: brand.primary, color: brand.accent }}
          >
            Asignar
          </button>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-teal-500/20" : "bg-teal-50"
            }`}
          >
            <i className="ri-truck-line text-teal-500 text-lg"></i>
          </div>
          <div className="min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {vehicleLabel(vehicle)}
            </p>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {vehicle.id}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="hidden md:block text-center min-w-[160px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
            >
              ID de acceso
            </p>
            <p
              className={`text-sm font-mono ${themeClasses.text} truncate max-w-[160px]`}
              title={vehicle.accessControl_id || ""}
            >
              {vehicle.accessControl_id || "—"}
            </p>
          </div>
          <div className="hidden lg:block min-w-[180px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary} mb-1`}
            >
              Patios
            </p>
            <div className="flex flex-wrap gap-1">
              {yards.length === 0 ? (
                <span
                  className={`text-xs italic ${themeClasses.textSecondary}`}
                >
                  Sin asignar
                </span>
              ) : (
                yards.slice(0, 3).map((id) => (
                  <span
                    key={id}
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${
                      isDarkMode
                        ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                    }`}
                  >
                    {yardsById[id] || id}
                  </span>
                ))
              )}
              {yards.length > 3 && (
                <span className={`text-[10px] ${themeClasses.textSecondary}`}>
                  +{yards.length - 3}
                </span>
              )}
            </div>
          </div>

          <div className="hidden xl:block min-w-[160px]">
            <p
              className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary} mb-1`}
            >
              Tipo (grupo)
            </p>
            <div className="flex flex-wrap gap-1">
              {vehicleTypes.length === 0 ? (
                <span
                  className={`text-xs italic ${themeClasses.textSecondary}`}
                >
                  Sin tipo
                </span>
              ) : (
                vehicleTypes.map((t) => (
                  <span
                    key={t}
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${
                      isDarkMode
                        ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                        : "bg-cyan-50 text-cyan-700 border border-cyan-200"
                    }`}
                  >
                    {getWaterTypeLabel(t)}
                  </span>
                ))
              )}
            </div>
          </div>

          <button
            onClick={() => onAssign(vehicle)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ backgroundColor: brand.primary, color: brand.accent }}
          >
            <i className="ri-links-line mr-1"></i>
            Asignar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assignment Modal ────────────────────────────────────────────────────────

function VehicleAssignModal({
  themeClasses,
  isDarkMode,
  vehicles,
  yards,
  initialVehicle,
  vehicleTypesById,
  isSubmitting,
  accessControlLocked,
  onClose,
  onSubmit,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  vehicles: TruckYardVehicle[];
  yards: { id: string; name: string }[];
  initialVehicle: TruckYardVehicle | null;
  vehicleTypesById: Record<string, string[]>;
  isSubmitting: boolean;
  /** Operador de patio: el backend le responde 403 si cambia el tag. */
  accessControlLocked: boolean;
  onClose: () => void;
  onSubmit: (data: {
    vehicleId: string;
    yard_ids: string[];
    accessControl_id: string;
    truck_yard_types: string[];
    ppd: string;
    nis: string;
    capacidad: string;
    is_provider: boolean;
    from: string;
    to: string;
  }) => void;
}) {
  const lockedVehicle = !!initialVehicle;
  const { t } = useTranslation("truckYard");

  const [vehicleId, setVehicleId] = useState<string>(initialVehicle?.id || "");
  const [yardIds, setYardIds] = useState<string[]>(
    initialVehicle?.yard_ids || [],
  );
  const [accessControlId, setAccessControlId] = useState<string>(
    initialVehicle?.accessControl_id || "",
  );
  const [ppd, setPpd] = useState<string>(initialVehicle?.ppd || "");
  const [nis, setNis] = useState<string>(initialVehicle?.nis || "");
  const [capacidad, setCapacidad] = useState<string>(
    initialVehicle?.capacidad || "",
  );
  const [isProvider, setIsProvider] = useState<boolean>(
    initialVehicle?.is_provider === true,
  );
  const [fromDate, setFromDate] = useState<string>(initialVehicle?.from || "");
  const [toDate, setToDate] = useState<string>(initialVehicle?.to || "");
  // Per-vehicle type subset (⊆ inherited from groups). Initialized below
  // from initialVehicle/effectiveTypes once vehicleId + inherited are known.
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const inheritedTypes = vehicleId ? vehicleTypesById[vehicleId] || [] : [];

  // Seed selectedTypes when the active vehicle changes:
  //   - If the vehicle already has a saved subset, use it (filtered against
  //     the current inherited pool — in case a group dropped a type since).
  //   - Otherwise default to all inherited (matches the read-only behavior
  //     this modal had before, so first save preserves what the user saw).
  //   - If the filter would leave nothing selected, fall back to all
  //     inherited so the "never empty" invariant holds.
  useEffect(() => {
    if (!vehicleId) {
      setSelectedTypes([]);
      return;
    }
    const found = vehicles.find((v) => v.id === vehicleId);
    const stored = Array.isArray(found?.truck_yard_types)
      ? found!.truck_yard_types
      : null;
    const next = stored
      ? stored.filter((t) => inheritedTypes.includes(t))
      : [...inheritedTypes];
    setSelectedTypes(next.length > 0 ? next : [...inheritedTypes]);
    // inheritedTypes is derived from vehicleId + vehicleTypesById; depend
    // on the source values to avoid re-running this every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, vehicles, vehicleTypesById]);

  // If the selected vehicle changes (only relevant when not locked), prefill
  // assignment fields with the values already stored on that vehicle.
  useEffect(() => {
    if (lockedVehicle) return;
    if (!vehicleId) {
      setYardIds([]);
      setAccessControlId("");
      setPpd("");
      setNis("");
      setCapacidad("");
      setFromDate("");
      setToDate("");
      setIsProvider(false);
      return;
    }
    const found = vehicles.find((v) => v.id === vehicleId);
    if (found) {
      setYardIds(found.yard_ids || []);
      setAccessControlId(found.accessControl_id || "");
      setPpd(found.ppd || "");
      setNis(found.nis || "");
      setCapacidad(found.capacidad || "");
      setFromDate(found.from || "");
      setToDate(found.to || "");
      // Belongs in the prefill like every other stored field: handleSubmit
      // always sends is_provider, so leaving it at its initial value would
      // un-flag a supplier unit the moment anyone saved its assignment from
      // this modal — and would move the flag onto whichever unit was picked
      // after ticking the box.
      setIsProvider(found.is_provider === true);
    }
  }, [vehicleId, lockedVehicle, vehicles]);

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const cardCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  } mb-1.5 block`;

  const vehicleOptions = useMemo(
    () =>
      vehicles.map((v) => ({
        value: v.id,
        label: vehicleLabel(v),
      })),
    [vehicles],
  );

  const yardOptions: YardOption[] = useMemo(
    () => yards.map((y) => ({ value: y.id, label: y.name })),
    [yards],
  );

  const selectedYardOptions = yardOptions.filter((o) =>
    yardIds.includes(o.value),
  );

  const validate = (): boolean => {
    const e: Partial<Record<string, string>> = {};
    if (!vehicleId) e.vehicleId = "Seleccione un vehículo";
    if (yardIds.length === 0) e.yard_ids = "Seleccione al menos un patio";
    // Only enforce when the group provides at least one inherited type —
    // vehicles in groups with no types simply have nothing to assign.
    if (inheritedTypes.length > 0 && selectedTypes.length === 0) {
      e.type = "Seleccione al menos un tipo";
    }
    // Only validate the range when both ends are set; either end alone is
    // allowed (open-ended window).
    if (fromDate && toDate && toDate < fromDate) {
      e.to = "La fecha 'Hasta' no puede ser anterior a 'Desde'";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      vehicleId,
      yard_ids: yardIds,
      accessControl_id: accessControlId.trim(),
      truck_yard_types: selectedTypes,
      ppd: ppd.trim(),
      nis: nis.trim(),
      capacidad: capacidad.trim(),
      is_provider: isProvider,
      from: fromDate,
      to: toDate,
    });
  };

  const toggleType = (t: string) => {
    setSelectedTypes((prev) => {
      if (prev.includes(t)) {
        // Block removal of the last remaining chip to keep the invariant.
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== t);
      }
      return [...prev, t];
    });
    if (errors.type) setErrors((p) => ({ ...p, type: undefined }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            Asignar vehículo a patios
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className={labelCls}>
              Vehículo <span className="text-red-500">*</span>
            </label>
            {lockedVehicle ? (
              <div
                className={`${cardCls} flex items-center gap-3 cursor-not-allowed opacity-90`}
              >
                <i className="ri-truck-line text-teal-500"></i>
                <div className="min-w-0">
                  <p className={`text-sm ${themeClasses.text} truncate`}>
                    {vehicleLabel(initialVehicle!)}
                  </p>
                  <p
                    className={`text-[10px] font-mono ${themeClasses.textSecondary}`}
                  >
                    {initialVehicle!.id}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <Select
                  options={vehicleOptions}
                  styles={getCustomSelectStyles(isDarkMode)}
                  placeholder="Buscar y seleccionar un vehículo..."
                  value={
                    vehicleOptions.find((o) => o.value === vehicleId) || null
                  }
                  onChange={(option: any) => {
                    setVehicleId(option?.value || "");
                    if (errors.vehicleId)
                      setErrors((prev) => ({ ...prev, vehicleId: undefined }));
                  }}
                  isSearchable
                  isClearable
                  noOptionsMessage={() => "No hay vehículos disponibles"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {errors.vehicleId && (
                  <p className="text-xs text-red-400 mt-1">
                    {errors.vehicleId}
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <label className={labelCls}>
              Patios asignados <span className="text-red-500">*</span>
            </label>
            <Select
              isMulti
              options={yardOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder="Seleccione uno o más patios..."
              value={selectedYardOptions}
              onChange={(opts: any) => {
                setYardIds((opts || []).map((o: YardOption) => o.value));
                if (errors.yard_ids)
                  setErrors((prev) => ({ ...prev, yard_ids: undefined }));
              }}
              isSearchable
              noOptionsMessage={() => "No hay patios disponibles"}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.yard_ids && (
              <p className="text-xs text-red-400 mt-1">{errors.yard_ids}</p>
            )}
          </div>

          <div>
            <label className={labelCls}>Tipo (heredado del grupo)</label>
            <div
              className={`${cardCls} flex flex-wrap gap-1.5 items-center min-h-[44px]`}
            >
              {inheritedTypes.length === 0 ? (
                <span
                  className={`text-xs italic ${themeClasses.textSecondary}`}
                >
                  Sin tipo asignado al grupo de este vehículo
                </span>
              ) : (
                inheritedTypes.map((t) => {
                  const active = selectedTypes.includes(t);
                  const isLastActive = active && selectedTypes.length === 1;
                  const activeCls = isDarkMode
                    ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/40"
                    : "bg-cyan-50 text-cyan-700 border-cyan-200";
                  const inactiveCls = isDarkMode
                    ? "bg-transparent text-gray-500 border-gray-700 hover:border-cyan-500/40"
                    : "bg-transparent text-gray-500 border-gray-300 hover:border-cyan-400";
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => toggleType(t)}
                      title={
                        isLastActive
                          ? "Debe quedar al menos un tipo seleccionado"
                          : active
                            ? "Quitar este tipo del vehículo"
                            : "Agregar este tipo al vehículo"
                      }
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border transition-colors ${
                        active ? activeCls : inactiveCls
                      } ${isLastActive ? "cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <i
                        className={`${
                          active ? "ri-check-line" : "ri-add-line"
                        } text-xs`}
                      ></i>
                      {getWaterTypeLabel(t)}
                    </button>
                  );
                })
              )}
            </div>
            {errors.type && (
              <p className="text-xs text-red-400 mt-1">{errors.type}</p>
            )}
            <p className={`text-xs ${themeClasses.textSecondary} mt-1.5`}>
              Solo puedes elegir entre los tipos del grupo. Para agregar más,
              edita el grupo desde la sección Grupos.
            </p>
          </div>

          <div>
            <label className={labelCls}>ID de control de acceso</label>
            <input
              type="text"
              value={accessControlId}
              onChange={(e) => setAccessControlId(e.target.value)}
              disabled={accessControlLocked}
              className={`${cardCls}${accessControlLocked ? " opacity-60 cursor-not-allowed" : ""}`}
              placeholder="Ej. TAG-0001, RFID-AB12, placa-XYZ..."
            />
            <p className={`text-xs ${themeClasses.textSecondary} mt-1.5`}>
              {accessControlLocked
                ? t("vehicles.accessControlLockedHint")
                : "Cadena libre que será emparejada por el control de acceso."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>PPD</label>
              <input
                type="text"
                value={ppd}
                onChange={(e) => setPpd(e.target.value)}
                className={cardCls}
                placeholder="Ej. PPD-0001"
              />
            </div>
            <div>
              <label className={labelCls}>NIS</label>
              <input
                type="text"
                value={nis}
                onChange={(e) => setNis(e.target.value)}
                className={cardCls}
                placeholder="Ej. 90000001"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Capacidad del contenedor (m³)</label>
              <input
                type="number"
                min={0}
                step="any"
                value={capacidad}
                onChange={(e) => setCapacidad(e.target.value)}
                className={cardCls}
                placeholder="Ej. 10"
              />
            </div>
          </div>

          {/* Proveedor: la unidad cruza la pluma pero no descarga, así que su
              visita abre una inspección que nace CANCELADA. */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isProvider}
                onChange={(e) => setIsProvider(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-primary-500 cursor-pointer"
              />
              <span>
                <span className={`block text-sm font-medium ${themeClasses.text}`}>
                  Es proveedor
                </span>
                <span
                  className={`block text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Sus entradas se registran con folio, pero la inspección se
                  crea cancelada: un proveedor no descarga agua y no hay nada
                  que inspeccionar.
                </span>
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Desde</label>
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  if (errors.to) setErrors((p) => ({ ...p, to: undefined }));
                }}
                className={cardCls}
              />
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => {
                  setToDate(e.target.value);
                  if (errors.to) setErrors((p) => ({ ...p, to: undefined }));
                }}
                className={cardCls}
              />
              {errors.to && (
                <p className="text-xs text-red-400 mt-1">{errors.to}</p>
              )}
            </div>
          </div>

          <div
            className={`flex justify-end gap-3 pt-4 border-t ${themeClasses.cardBorder}`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.buttonText} font-medium text-sm transition-colors`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Guardando...
                </>
              ) : (
                "Guardar asignación"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────────────

function VehiclesPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <PaginationControls
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({
  themeClasses,
  isDarkMode,
  onAdd,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
          isDarkMode ? "bg-dark-card" : "bg-gray-100"
        }`}
      >
        <i
          className={`ri-truck-line text-4xl ${themeClasses.textSecondary}`}
        ></i>
      </div>
      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
        Sin vehículos asignados
      </h3>
      <p className={`text-sm ${themeClasses.textSecondary} mb-4`}>
        No se encontraron vehículos con los filtros actuales.
      </p>
      <button
        onClick={onAdd}
        className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
        style={{ backgroundColor: brand.primary, color: brand.accent }}
      >
        <i className="ri-add-line mr-1"></i>
        Asignar vehículo
      </button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function VehiclesSection() {
  const { isDarkMode } = useDarkMode();
  const {
    vehicles,
    yards,
    fetchYards,
    assignVehicleToYards,
  } = useTruckYardStore();
  const groups = useGroupsStore((state) => state.groups);
  const vehiclesQuery = useAllTruckYardVehicles();
  const vehiclesLoading = vehiclesQuery.isLoading;
  const queryClient = useQueryClient();
  useAllGroups();
  const { getAssignedYardIds, hasYardRestrictions, hasNoAssignedYards, userRoleId } =
    useTruckYardRole();
  const { t } = useTranslation("truckYard");
  const accessControlLocked = !canEditAccessControlId(userRoleId);

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const [searchQuery, setSearchQuery] = useState("");
  // Selección múltiple de patios: [] = todos (los del alcance), NO_YARD_FILTER
  // suma los vehículos sin patio. Pasa el que tenga CUALQUIERA de los elegidos.
  const [selectedYards, setSelectedYards] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [assignVehicle, setAssignVehicle] = useState<TruckYardVehicle | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchYards();
  }, [fetchYards]);

  const assignedYardIds = getAssignedYardIds();

  // For supervisors, restrict yard options to those they manage.
  // Sin el `&& assignedYardIds.length > 0` que había acá: en un rol restringido
  // la lista vacía significa "sin patios asignados", no "sin restricción"
  // (issue #497) — antes apagaba el recorte y ofrecía todos los patios.
  const yardOptionsForSelect = useMemo(() => {
    if (hasYardRestrictions()) {
      return yards
        .filter((y) => assignedYardIds.includes(y.id))
        .map((y) => ({ id: y.id, name: y.name }));
    }
    return yards.map((y) => ({ id: y.id, name: y.name }));
  }, [yards, hasYardRestrictions, assignedYardIds]);

  const yardsById = useMemo(() => {
    const map: Record<string, string> = {};
    yards.forEach((y) => (map[y.id] = y.name));
    return map;
  }, [yards]);

  // Build a set of vehicle ids that belong to at least one group (for "no group" filter).
  // Join on `vehicle_ids` (backend-resolved from devices_ids → device.vehicle_id),
  // since devices_ids are device document IDs, not vehicle IDs.
  const vehiclesInAnyGroup = useMemo(() => {
    const set = new Set<string>();
    groups.forEach((g) => (g.vehicle_ids || []).forEach((id) => set.add(id)));
    return set;
  }, [groups]);

  // Inherited water types per vehicle, computed from the vehicle's groups.
  // A vehicle may be in multiple groups; the union of types is shown.
  // Joins by `vehicle_ids` (resolved server-side from devices_ids → device.vehicle_id),
  // since the raw devices_ids are device document IDs, not vehicle IDs.
  const vehicleTypesById = useMemo(() => {
    const map: Record<string, string[]> = {};
    groups.forEach((g) => {
      const groupTypes = g.type || [];
      if (groupTypes.length === 0) return;
      (g.vehicle_ids || []).forEach((vehicleId) => {
        const existing = map[vehicleId] || [];
        const merged = Array.from(new Set([...existing, ...groupTypes]));
        map[vehicleId] = merged;
      });
    });
    return map;
  }, [groups]);

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      // Supervisors only see vehicles assigned to one of their yards.
      // Sin patios asignados no ven ninguno (issue #497).
      if (hasYardRestrictions()) {
        const vehicleYards = v.yard_ids || [];
        const overlap = vehicleYards.some((id) => assignedYardIds.includes(id));
        if (!overlap) return false;
      }

      const matchSearch =
        !searchQuery ||
        (v.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.plate || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.accessControl_id || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        v.id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchYard = matchesYardSelection(v.yard_ids, selectedYards);

      let matchGroup = true;
      if (selectedGroup) {
        if (selectedGroup === "none") {
          matchGroup = !vehiclesInAnyGroup.has(v.id);
        } else {
          const group = groups.find((g) => g.id === selectedGroup);
          // Match on vehicle_ids (backend-resolved), not devices_ids (device
          // document IDs) — v.id is a vehicle ID.
          matchGroup = !!group?.vehicle_ids?.includes(v.id);
        }
      }

      return matchSearch && matchYard && matchGroup;
    });
  }, [
    vehicles,
    searchQuery,
    selectedYards,
    selectedGroup,
    groups,
    vehiclesInAnyGroup,
    hasYardRestrictions,
    assignedYardIds,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleOpenAdd = () => {
    setAssignVehicle(null);
    setFormOpen(true);
  };

  const handleOpenAssign = (vehicle: TruckYardVehicle) => {
    setAssignVehicle(vehicle);
    setFormOpen(true);
  };

  const handleSubmit = async (data: {
    vehicleId: string;
    yard_ids: string[];
    accessControl_id: string;
    truck_yard_types: string[];
    ppd: string;
    nis: string;
    capacidad: string;
    is_provider: boolean;
    from: string;
    to: string;
  }) => {
    setIsSubmitting(true);
    try {
      // Device ids the client already resolved for this vehicle (from
      // /vehicleList). Sent so the backend can mirror them into the patio's
      // geofence even without re-querying. Empty when the vehicle has no GPS
      // device — in that case nothing can be tracked by the geofence.
      const vehicleDeviceIds = (
        vehicles.find((v) => v.id === data.vehicleId)?.devices || []
      )
        .map((d) => d.id)
        .filter(Boolean);

      const updated = await assignVehicleToYards(data.vehicleId, {
        yard_ids: data.yard_ids,
        accessControl_id: data.accessControl_id,
        truck_yard_types: data.truck_yard_types,
        ppd: data.ppd,
        nis: data.nis,
        capacidad: data.capacidad,
        is_provider: data.is_provider,
        from: data.from,
        to: data.to,
        device_ids: vehicleDeviceIds,
      });
      if (!updated) throw new Error("assignVehicleToYards failed");
      Swal.fire({
        icon: "success",
        title: "Asignación guardada",
        text: "El vehículo se ha asignado correctamente",
        confirmButtonColor: brand.primary,
      });
      setFormOpen(false);
      setAssignVehicle(null);
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: t(assignVehicleErrorKey(error)),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sin entrada "Todos": en un multi, "todos" es no elegir nada.
  const yardFilterOptions = useMemo(
    () => [
      ...yardOptionsForSelect.map((y) => ({ value: y.id, label: y.name })),
      { value: NO_YARD_FILTER, label: "Sin patio asignado" },
    ],
    [yardOptionsForSelect],
  );

  const groupFilterOptions = useMemo(
    () => [
      { value: "all", label: "Todos los grupos" },
      { value: "none", label: "Sin grupo" },
      ...groups.map((g) => ({ value: g.id, label: g.name })),
    ],
    [groups],
  );

  return (
    <div className="h-full flex flex-col relative">
      <HeaderComponent
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        title="Vehículos"
        subtitle="Asigna vehículos a patios y configura su control de acceso"
        searchQuery={searchQuery}
        // El patio ocupaba el combo de "estado"; ahora es un filtro múltiple en
        // `extraFilters` y esta pantalla no tiene estado que filtrar.
        showStatusFilter={false}
        selectedStatus=""
        selectedView={selectedView}
        onSearchChange={(v: string) => {
          setSearchQuery(v);
          setCurrentPage(1);
        }}
        onStatusChange={() => {}}
        onViewChange={setSelectedView}
        onAdd={handleOpenAdd}
        addButtonText="Asignar vehículo"
        addButtonShortText="Asignar"
        searchPlaceholder="Buscar vehículo, placa o ID de acceso..."
        extraFilters={[
          {
            multi: true,
            values: selectedYards,
            onChange: (v: string[]) => {
              setSelectedYards(v);
              setCurrentPage(1);
            },
            options: yardFilterOptions,
            placeholder: "Todos los patios",
            noOptionsMessage: "Sin patios disponibles",
          },
          {
            value: selectedGroup,
            onChange: (v: string) => {
              setSelectedGroup(v);
              setCurrentPage(1);
            },
            options: groupFilterOptions,
            placeholder: "Todos los grupos",
            noOptionsMessage: "Sin grupos disponibles",
          },
        ]}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {hasNoAssignedYards() && (
          <div className="mb-4">
            <NoAssignedYardsNotice
              isDarkMode={isDarkMode}
              what="ningún vehículo"
            />
          </div>
        )}
        {vehiclesLoading ? (
          <div
            className={`py-16 text-center text-sm ${themeClasses.textSecondary}`}
          >
            Cargando vehículos...
          </div>
        ) : paginated.length === 0 ? (
          <EmptyState
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onAdd={handleOpenAdd}
          />
        ) : selectedView === "list" ? (
          <div className="space-y-3">
            {paginated.map((v) => (
              <VehicleListRow
                key={v.id}
                vehicle={v}
                yardsById={yardsById}
                vehicleTypes={effectiveTypesFor(
                  v,
                  vehicleTypesById[v.id] || [],
                )}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onAssign={handleOpenAssign}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map((v) => (
              <VehicleGridCard
                key={v.id}
                vehicle={v}
                yardsById={yardsById}
                vehicleTypes={effectiveTypesFor(
                  v,
                  vehicleTypesById[v.id] || [],
                )}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onAssign={handleOpenAssign}
              />
            ))}
          </div>
        )}

        <VehiclesPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {formOpen && (
        <VehicleAssignModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          vehicles={vehicles}
          yards={yardOptionsForSelect}
          initialVehicle={assignVehicle}
          vehicleTypesById={vehicleTypesById}
          isSubmitting={isSubmitting}
          accessControlLocked={accessControlLocked}
          onClose={() => {
            setFormOpen(false);
            setAssignVehicle(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
